import React, { type ReactNode } from 'react';
import 'url-search-params-polyfill';
import styled from 'styled-components';
import Head from 'next/head';
import type { GlobalPageData } from '@lib/page-data/global.map';
import type { RequestData, UserSessionData } from '@lib/data-fetching';
import PageContainer, { type PageStateModifiers } from './PageContainer';
import MainLayout from '../../../templates/MainLayout';
import MenuWrapper from '../MenuWrapper/MenuWrapper';
import Footer from '../Footer/Footer';
import GlobalBanner from '../Banner/GlobalBanner';
import Modal from '../Modal/Modal';
import {
  resolveInterstitialURI,
  getInitAdSlotsScript,
} from '../../../../lib/utils';
import OnboardingModalContainer from '../OnboardingModal/OnboardingModalContainer';
import SessionExtensionContainer from '../SessionExtension/SessionExtensionContainer';
import { AnalyticsPageContext } from './AnalyticsPageContext';

export type PageProps = {
  globalPageData: GlobalPageData;
  requestData: RequestData;
  userSessionData: UserSessionData;
  children?: ReactNode;
  pageName?: string;
  pageTitle?: string | null;
  metaDescription?: string | null;
  analyticsPageName?: string;
  pageAds?: unknown[] | null;
  affiliateUrl?: string;
  shopSlug?: string;
  offerSlug?: string;
  adSlug?: string;
};

export type PageInjectedProps = {
  userShopsData: UserSessionData['userShopsData'];
  pageStateModifiers: PageStateModifiers;
  userData: UserSessionData['userData'];
  globalPageConfig: GlobalPageData['globalPageConfig'];
};

const PageBodyDiv = styled.div`
  min-height: calc(100vh - 504px);
`;

const hasOnboardingData = (onboardingModalData: GlobalPageData['onboardingModal']) => {
  if (!onboardingModalData || typeof onboardingModalData !== 'object' || Array.isArray(onboardingModalData)) {
    return false;
  }

  const {
    headerText,
    subHeaderText,
    buttonText,
    slots,
    bottomBannerHeaderText,
    bottomBannerSubHeaderText,
  } = onboardingModalData;

  return Boolean(
    headerText
    || subHeaderText
    || buttonText
    || bottomBannerHeaderText
    || bottomBannerSubHeaderText
    || (Array.isArray(slots) && slots.length > 0),
  );
};

class Page extends PageContainer<PageProps> {
  componentDidMount() {
    super.componentDidMount();
    const {
      requestData,
      pageAds = [],
      userSessionData,
      requestData: {
        urlLocale: locale,
        urlShowSignIn,
      },
    } = this.props;

    const {
      affiliateUrl,
      shopSlug,
      offerSlug,
      adSlug,
    } = requestData.urlQueryParams;

    // Should be moved server-side
    if (urlShowSignIn && !this.pageStateModifiers.isUserLoggedIn()) {
      // The redirect handles its own error and the page continues rendering.
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.pageStateModifiers.makeAndOpenSignInModal();
      return;
    }
    if (urlShowSignIn && this.pageStateModifiers.isUserLoggedIn()) {
      const redirectAffiliateUrl = this.props.affiliateUrl || affiliateUrl;
      const shopSlugToUse = this.props.shopSlug || shopSlug;
      const offerSlugToUse = this.props.offerSlug || offerSlug;
      const adSlugToUse = this.props.adSlug || adSlug;

      if (redirectAffiliateUrl) {
        const resolvedURIObj = resolveInterstitialURI({
          shopSlug: shopSlugToUse,
          offerSlug: offerSlugToUse,
          adSlug: adSlugToUse,
          locale,
        });
        window.location.href = resolvedURIObj.externalRedirectUrl;
      }
    }

    if (pageAds && pageAds.length > 0 && 'adsEnabled' in window && window.adsEnabled) {
      const intitAdSlotsScript = getInitAdSlotsScript(pageAds, userSessionData.userData?.ppid);
      if (intitAdSlotsScript) {
        const script = window.document.createElement('script');
        script.textContent = intitAdSlotsScript;
        window.document.head.append(script);
      }
    }
  }

  render() {
    const {
      pageName = '',
      pageTitle = '',
      metaDescription,
      globalPageData,
      requestData,
      userSessionData,
      analyticsPageName,
    } = this.props;

    const {
      urlLocale,
      urlData,
      urlQueryParams,
    } = requestData;

    const {
      userData,
    } = userSessionData;

    const {
      searchNavBarData,
      catNavBarData,
      globalNavData,
      footerData,
      bannerData,
      notificationData,
      onboardingModal,
      defaultMetaDescription,
      pageTitleBase,
      globalPageConfig,
    } = globalPageData;

    const metaDescriptionToUse = metaDescription ?? defaultMetaDescription;

    const pageTitleToUse = `${pageTitle} ${pageTitleBase}`;
    const canonicalLink = urlData?.origin && urlData?.pathname
      ? `${urlData.origin}${urlData.pathname}`
      : '';

    const childrenWithProps = React.Children.map(
      this.props.children,
      (child) => (React.isValidElement<Partial<PageInjectedProps>>(child) ? React.cloneElement(child, {
        userShopsData: this.state.userShopsData,
        pageStateModifiers: this.pageStateModifiers,
        userData: this.state.userData,
        globalPageConfig,
      }) : child),
    );

    this.globalPageConfig = globalPageConfig;
    this.onboardingModal = hasOnboardingData(onboardingModal) ? onboardingModal ?? null : null;

    const analyticsPageContextValue = {
      analyticsPageName,
      userData: this.state.userData,
      locale: urlLocale,
    };

    const pageContent = (
      <MainLayout>
        {/* Head */}
        <Head>
          <title>{pageTitleToUse}</title>
          <meta name="description" content={metaDescriptionToUse} />
          {canonicalLink && <link rel="canonical" href={canonicalLink} />}
        </Head>

        <MenuWrapper
          pageName={pageName}
          searchNavBarData={searchNavBarData}
          catNavBarData={catNavBarData}
          globalNavData={globalNavData}
          userShopsData={this.state.userShopsData}
          locale={urlLocale}
          isUserLoggedIn={this.pageStateModifiers.isUserLoggedIn}
          pageStateModifiers={this.pageStateModifiers}
          selectedDropDown={this.state.selectedDropDown}
          localeUrls={urlQueryParams.localeUrls || {}}
          subCategoryLocaleSlugMap={urlQueryParams.subCategoryLocaleSlugMap || {}}
          globalPageConfig={globalPageConfig}
          notificationData={notificationData}
          userData={userData}
        >
          <main id="main-content">
            <PageBodyDiv>
              {childrenWithProps}
            </PageBodyDiv>
          </main>

          {/* Footer */}
          <Footer
            locale={urlLocale}
            {...footerData}
          />

          {/* Global Banner */}
          <GlobalBanner
            {...bannerData}
            locale={urlLocale}
            accessibilityLabel={globalPageConfig?.globalBannerLabel}
            closeIconLabel={globalPageConfig?.closeIconLabel}
          />
        </MenuWrapper>

        {/* Only instance of modal for the page */}
        <Modal
          ref={(element) => { this.modal = element; }}
          urlQueryParams={urlQueryParams}
          {...globalPageConfig}
        />

        <OnboardingModalContainer
          pageStateModifiers={this.pageStateModifiers}
          requestData={requestData}
        />

        <SessionExtensionContainer
          pageStateModifiers={this.pageStateModifiers}
          locale={urlLocale}
          {...globalPageConfig}
        />
      </MainLayout>
    );

    return (
      <AnalyticsPageContext.Provider value={analyticsPageContextValue}>
        {pageContent}
      </AnalyticsPageContext.Provider>
    );
  }
}

export default Page;
